import { Box } from '@mui/material';
import { Form, Formik } from 'formik';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import schemas from '../../../config/schemas';
import { stepsLabels } from '../../../data/paymentSteps';
import usePaymentStepper from '../../../hooks/usePaymentStepper';
import { RootState } from '../../../store/config';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setCommerce } from '../../../store/slices/commerce.slice';
import { setServicesDebt } from '../../../store/slices/debt.slice';
import { Commerce } from '../../../types/commerce.type';
import { ServicesDebt } from '../../../types/debt.types';
import { PaymentStep } from '../../../types/enums';
import { Issuer } from '../../../types/paymentMethod.types';
import { Vertical } from '../../../types/verticals.type';
import { amountHelper } from '../../../utils/amount.helper';
import { commerceHelper } from '../../../utils/commerce.helper';
import { countryHelper } from '../../../utils/country.helper';
import { currencyHelper } from '../../../utils/currency.helper';
import FormikAutocompleteField from '../../Formik/FormikAutocompleteField';
import FormikNumberFormat from '../../Formik/FormikNumberFormat';
import FormikSelectField from '../../Formik/FormikSelectField';
import FormikTextField from '../../Formik/FormikTextField';

function PaymentInformation() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setStep, isPaymentLink, updateSubmitDisabled } = usePaymentStepper();
  const vertical: Vertical | null = useAppSelector((state: RootState) => state.vertical.vertical);
  const commerces: Commerce[] | undefined = vertical?.commerces;
  const commerce: Commerce | null = useAppSelector((state: RootState) => state.commerce.commerce);
  const issuer: Issuer | null = useAppSelector((state: RootState) => state.issuer.issuer);

  const servicesDebt: ServicesDebt = useAppSelector((state: RootState) => state.debt.servicesDebt);
  const { amount: amountParam, currency: currencyParam } = useParams<{
    amount?: string;
    currency?: string;
  }>();
  const amount = amountParam && +amountParam;
  const currency = currencyParam && amountHelper.getCurrencyCode(currencyParam?.toUpperCase());

  const submitServicesDebt = (values: ServicesDebt) => {
    const selectedCommerce: Commerce | undefined = commerces?.find(
      (commerce) => commerce.name === values.commerceName,
    );
    dispatch(setServicesDebt(values));
    if (selectedCommerce) {
      dispatch(setCommerce(selectedCommerce));
    }
    dispatch(setStep(PaymentStep.PAYMENT_METHODS));
  };

  const handleOnChangeCurrency = (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: string) => void,
    setValues: (values: any) => void,
    values: any,
  ) => {
    setFieldValue('currency', event.target.value);
    currencyHelper.onCurrencyChange(event, setValues, values);
  };

  const initialValues: ServicesDebt = {
    commerceName: commerce?.name || null,
    currency: currency || servicesDebt.currency || 'Seleccionar',
    amount: amount || servicesDebt.amount || null,
    serviceInvoice: servicesDebt.serviceInvoice || '',
  };

  if (!commerce && isPaymentLink) return null;

  return (
    <Box id="services-form-container">
      <h6>{t(stepsLabels[0])}</h6>
      <Formik
        initialValues={initialValues}
        validationSchema={schemas.ServicesDebtSchema}
        onSubmit={submitServicesDebt}
        enableReinitialize
        isInitialValid={schemas.ServicesDebtSchema.isValidSync(initialValues)}
      >
        {({ values, setFieldValue, isValid, setValues }) => {
          updateSubmitDisabled(!isValid);

          return (
            <Form id="services-debt-form">
              <FormikAutocompleteField
                name="commerceName"
                fullWidth
                label={t('verticals.services.debt-form.labels.commerceName')}
                options={commerceHelper.sortVerticalCommerces(commerces) || []}
                placeholder={t('verticals.services.debt-form.placeholders.commerceName')}
                value={values.commerceName || commerce?.name}
                disabled={isPaymentLink && !!commerce?.name}                
              />
             
              <FormikSelectField
                name="currency"
                fullWidth
                label={t('verticals.services.debt-form.labels.currency')}
                options={countryHelper.getCurrencyOptions(issuer)}
                placeholder={t('verticals.services.debt-form.placeholders.currency')}
                onChange={(event) => {
                  handleOnChangeCurrency(event, setFieldValue, setValues, values);
                }}
                disabled={isPaymentLink && !!currency}
              />
              <FormikNumberFormat
                fullWidth
                name="amount"
                label={t('verticals.services.debt-form.labels.amount')}
                disabled={isPaymentLink && !!amount}
              />
              <FormikTextField
                fullWidth
                name="serviceInvoice"
                label={t('verticals.services.debt-form.labels.serviceInvoice')}
                type="text"
                inputMode="text"
              />
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
}

export default PaymentInformation;
